import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {DxPopupComponent} from "devextreme-angular/ui/popup";
import {
    DetectMethodChanges,
    DetectSetterChanges,
    DxValueChanged,
    escapeHtml,
    isValidNumber,
    isVoid,
    jsonCloneObject
} from "../utils";
import {SupportResistanceLevel, ZoneEditorRow, ZoneIndicatorApiService} from "./model/model";
import {SessionService} from "../authentication/session-service.service";
import {ZoneLevelsPopoverComponent} from "./popover/zone-levels-popover.component";
import {DxScrollViewComponent} from "devextreme-angular/ui/scroll-view";
import {MessageBusService} from "../message-bus.service";
import {
    CurrentZoneLevel,
    CurrentZoneTemplateDescriptor,
    CurrentZoneTemplateType,
    CurrentZoneTemplateData, CurrentZoneTemplateAssignment, ClientPortfolioForCurrentZoneTemplate,
    CurrentZoneType3Settings
} from "./model/zone-template.class";
import {ApgPortfolio} from "../adjustment-pricing-grid/model/ApgPortfolio";
import {CurrentZoneService} from "./model/current-zone.service";
import * as Enumerable from "linq";
import {GenericConfirmationDialogComponent} from "../generic-confirmation-dialog/generic-confirmation-dialog.component";
import {GenericInputDialogComponent} from "../generic-input-dialog/generic-input-dialog.component";
import {GetPortfolioCurrentZoneDataReply, ZoneTypeGrantValue} from "./model/cqrs.class";
import {IndividualConfig, ToastrService} from "ngx-toastr";
import {ShellTodayService} from "../shell-clock/shell-today.service";

@Component({
    selector: 'ets-current-zone-indicator-component',
    templateUrl: 'current-zone-indicator.component.html',
    styleUrls: ['current-zone-indicator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CurrentZoneIndicatorComponent implements OnInit {

    constructor(
        private apiService: ZoneIndicatorApiService,
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _sessionService: SessionService,
        private readonly _messageBus: MessageBusService,
        private readonly _currentZoneService: CurrentZoneService,
        private readonly _toastr: ToastrService,
        private readonly _shellToday: ShellTodayService,
    ) {
    }

    @ViewChild('popup', {static: false}) popup: DxPopupComponent;
    @ViewChild('popover', {static: false}) popover: ZoneLevelsPopoverComponent;
    @Input() symbol: string = '';
    @Input() visible: boolean = false;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() onSaved = new EventEmitter<void>();
    @ViewChild('levelsScroll', {static: false}) levelsScroll: DxScrollViewComponent;
    // @ViewChild('renameTemplate', {static: false}) renameDialog: DxPopupComponent;
    @ViewChild('assignTemplatesDialog', {static: false}) assignTemplatesDialog: DxPopupComponent;
    @ViewChild('confd', {static: false}) confirmationDialog: GenericConfirmationDialogComponent;
    @ViewChild('genericInputDialog', {static: false}) inputDialog: GenericInputDialogComponent;

    private _templatesByPortfolio: Record<string, CurrentZoneTemplateDescriptor[]> = {};

    // selector
    selectedTemplateType: CurrentZoneTemplateType;
    templateList: CurrentZoneTemplateDescriptor[];
    selectedTemplate: CurrentZoneTemplateDescriptor;
    selectedTemplateData: CurrentZoneTemplateData;
    selectedPortfolio: ApgPortfolio;
    selectedTemplateCurrentZone: number;

    clientPortfoliosToAssign: any[] = [];
    templatesToAssign: Array<any> = [];
    templatesToAssignLoading: boolean;
    assignmentData: CurrentZoneTemplateAssignment[] = [];

    portfolioCurrentZoneData: GetPortfolioCurrentZoneDataReply;

    // #region CR-20260904-001, Zones Z6 - the per-trader zone type grant (BR-Z1)

    /** The caller's own grant, or null when they hold none. Absence is not Type 1. */
    private _ownZoneTypeGrant: ZoneTypeGrantValue = null;

    /**
     * Whether the caller is a super user. It does NOT decide which types are offered - the stored
     * grant does that for everyone (O36). It decides who may edit other people's grants.
     */
    private _isSuperUserGrant: boolean = false;

    /** One row per user in the Assign Templates dialog. */
    zoneTypeGrantRows: { userId: string, userName: string, zoneType: ZoneTypeGrantValue }[] = [];

    /**
     * The grants offerable on EVERY row of the Assign Templates dialog.
     *
     * Owner ruling 2026-09-13: exactly four - Type 1, Type 2, Type 3, All. "Both" and
     * "Type 2 + 3" were removed; stored rows holding them read back as "All" (backend migration).
     * Every row gets the same four, whoever the user is: it is the super user's responsibility to
     * pick the right one. (This replaces the 2026-09-06 per-row narrowing.) A regular user
     * granted Type 1 or All still never reaches the Type 1 editor - see availableZoneTypes.
     */
    readonly zoneTypeChoices: ZoneTypeGrantValue[] = ['Type 1', 'Type 2', 'Type 3', 'All'];

    private readonly _everyZoneType: CurrentZoneTemplateType[] = ['Type 1', 'Type 2', 'Type 3'];

    /** The zone types one stored grant value allows. Absence of a grant allows nothing. */
    private zoneTypesOfGrant(grant: ZoneTypeGrantValue | null): CurrentZoneTemplateType[] {
        switch (grant) {
            case 'All':
                return this._everyZoneType;
            case 'Type 1':
            case 'Type 2':
            case 'Type 3':
                return [grant];
            default:
                return [];
        }
    }

    /**
     * The zone types this trader may work with. Cause -> effect: the editor offers only these,
     * so a type the trader is not granted cannot be reached by any route.
     */
    get availableZoneTypes(): CurrentZoneTemplateType[] {
        // Owner ruling 2026-09-06: a super user administers BOTH types, so both are always
        // offered - what is stored against them does not narrow it. This reverses O36 ("the
        // stored grant governs for everyone, super user included"): under O36 a super user whose
        // own grant said "Type 2" lost the Type 1 editor entirely, which is what broke it.
        if (this._isSuperUserGrant) {
            return this._everyZoneType;
        }

        // Type 1 is a super-user editor, so a regular user never reaches it whatever their grant
        // says. Their grant still decides Type 2 and Type 3.
        return this.grantedZoneTypes().filter(type => type !== 'Type 1');
    }

    /**
     * The zone types this trader's STORED grant allows, before the super-user rule above narrows
     * them. Absence of a grant row is not "Type 1" - it is no grant, so nothing is offered.
     *
     * The footer read-out is unaffected either way: the grant governs the editor, not the read-out.
     */
    private grantedZoneTypes(): CurrentZoneTemplateType[] {
        return this.zoneTypesOfGrant(this._ownZoneTypeGrant);
    }

    /** CR-20260913-001. Type 3 has no level list: its ladder is generated from four parameters. */
    get isType3Selected(): boolean {
        return this.selectedTemplateType === 'Type 3';
    }

    /** Levels each side of the starting price when a new Type 3 template is created (owner ruling 2026-09-13, Q2). */
    private static readonly DefaultLevelsEachSide = 10;

    isZoneTypeAvailable(type: CurrentZoneTemplateType): boolean {
        return this.availableZoneTypes.indexOf(type) >= 0;
    }

    get canEditZoneTypeGrants(): boolean {
        return this._isSuperUserGrant;
    }

    // #endregion


    inputAttrs = {
        'style': 'text-align: center;'
    };

    validationErrors: { [key: number]: string } = {};

    // currentZone: number;

    closePrice: number;

    private _loading: boolean = false;
    get loading(): boolean {
        return this._loading;
    }

    @DetectSetterChanges()
    set loading(value: boolean) {
        this._loading = value;
    }

    get isNewButtonDisabled(): boolean {
        return isVoid(this.selectedTemplateType);
    }

    get isSaveButtonDisabled(): boolean {
        return !this.isTemplateSelected;
    }

    get isSaveAsButtonDisabled(): boolean {
        return !this.isTemplateSelected ||
            isVoid(this.selectedTemplate.id);
    }

    get isDeleteButtonDisabled(): boolean {
        return !this.isTemplateSelected;
    }

    get isAddLevelButtonDisabled(): boolean {
        return !this.isTemplateSelected || this.isType3Selected;
    }

    get isTemplateSelected(): boolean {
        return !isVoid(this.selectedTemplate);
    }

    get isTypeSelected(): boolean {
        return !isVoid(this.selectedTemplateType);
    }

    @DetectMethodChanges({isAsync: true})
    async ngOnInit() {

        this._messageBus.of<any>('ShareClosePrice')
            .subscribe(res => {
                res.payload.closePrice = this.closePrice;
            });

        this._messageBus.of<{ portfolio: ApgPortfolio }>('Apg.PortfolioSelected')
            .subscribe(res => {
                this.onPortfolioSelected(res.payload);
            });

        // CR-20260918-005: the day flipped (a QG replay cursor stepped or jumped to another day, or
        // the server day rolled). The shell re-evaluates the zone for that day on the read, so
        // re-read it; the close price follows the same day.
        this._shellToday.changed$
            .subscribe(() => this.onTodayChanged());

        this.closePrice = await this.apiService.getClosePrice();
    }

    private async onTodayChanged(): Promise<void> {
        // getClosePrice() serves its cached value; the day changed, so force the re-read.
        await this.apiService.refresh('SPX');
        this.closePrice = await this.apiService.getClosePrice();
        if (this.selectedPortfolio) {
            const portfolioId = `${this.selectedPortfolio.userId}@${this.selectedPortfolio.id}`;
            await this.loadCurrentZoneData(portfolioId);
        }
        this._changeDetector.markForCheck();
    }

    private mapLevelsToRows(levels: SupportResistanceLevel[]): ZoneEditorRow[] {
        return levels.map((level, index) => ({
            sequenceNumber: index + 1,
            level: {...level},
            isNew: false,
            isDeleted: false,
            isModified: false
        }));
    }

    private sortRows() {
        this.selectedTemplateData.levels = this.selectedTemplateData.levels
            .sort((a, b) => b.price - a.price);

        // Update sequence numbers
        this.selectedTemplateData.levels.forEach((row, index) => {
            row.sequenceNumber = index + 1;
        });
    }

    @DetectMethodChanges()
    addRow() {
        const newRow: CurrentZoneLevel = {
            sequenceNumber: this.selectedTemplateData.levels.length + 1,
        };

        this.selectedTemplateData.levels.push(newRow);
        this.sortRows();
        this.scrollToBottom();
    }

    private scrollToBottom() {
        setTimeout(() => {
            if (this.levelsScroll) {
                const instance = this.levelsScroll.instance;
                instance.scrollBy(1000);
            }
        }, 250);
    }

    @DetectMethodChanges()
    removeRow(row: CurrentZoneLevel) {
        const index = this.selectedTemplateData.levels.indexOf(row);
        if (index > -1) {
            this.selectedTemplateData.levels.splice(index, 1);
        }
        this.sortRows();
    }

    @DetectMethodChanges()
    onPriceChange(row: CurrentZoneLevel, value: any) {
        row.price = parseFloat(value) || 0;
        this.sortRows();
    }

    validateAllRows(): boolean {
        this.validationErrors = {};
        let isValid = true;

        // A Type 3 template validates its own block and nothing of the level list.
        if (this.isType3Selected) {
            return this.validateType3Parameters();
        }

        // Check for empty consecutive days
        if (!this.selectedTemplateData.settings.numOfCloses
            || this.selectedTemplateData.settings.numOfCloses < 1) {

            this._toastr.error('"Consecutive [x] Closes" is a Mandatory Parameter');

            return false;
        }

        const activePrices = this.selectedTemplateData.levels
            .map(row => row.price);

        if (isVoid(this.selectedTemplateData.levels) || this.selectedTemplateData.levels.length < 2) {
            this._toastr.error('Please provide at least two price levels');
            return false;
        }

        const hasEmpty = this.selectedTemplateData.levels.some((row) => !isValidNumber(row.price, true));

        if (hasEmpty) {
            this._toastr.error('Please provide prices for every level');
            return false;
        }


        const hasDups = this.selectedTemplateData.levels.some((row) => {
            // Check for duplicates
            const duplicateCount = activePrices.filter(p => p === row.price).length;
            return duplicateCount > 1;
        });

        if (hasDups) {
            this._toastr.error('Template cannot contain duplicate levels');
            return false;
        }

        return true;
    }

    /**
     * CR-20260913-001. The four Type 3 parameters, each a positive number; the bracket must be
     * narrower than half the spacing, or two adjacent bands would overlap and a close between two
     * lines could belong to either. The backend generates the ladder from these and refuses a
     * non-positive spacing with an EMPTY ladder, so it is stopped here, where the user can fix it.
     */
    private validateType3Parameters(): boolean {
        const s = this.selectedTemplateData.type3 || {};

        if (!isValidNumber(s.numOfCloses, true) || s.numOfCloses < 1) {
            this._toastr.error('"Consecutive [x] Closes" is a Mandatory Parameter');
            return false;
        }

        if (!isValidNumber(s.startingPrice, true) || s.startingPrice <= 0) {
            this._toastr.error('"Starting Price" is a Mandatory Parameter');
            return false;
        }

        if (!isValidNumber(s.zoneSpacing, true) || s.zoneSpacing <= 0) {
            this._toastr.error('"Zone Spacing" must be a positive number');
            return false;
        }

        if (!isValidNumber(s.bracketSize, true) || s.bracketSize <= 0) {
            this._toastr.error('"Bracket Size" must be a positive number');
            return false;
        }

        if (s.bracketSize * 2 >= s.zoneSpacing) {
            this._toastr.error('"Bracket Size" must be less than half of "Zone Spacing", or adjacent bands overlap');
            return false;
        }

        if (!isValidNumber(s.levelsEachSide, true) || s.levelsEachSide < 1) {
            this._toastr.error('"Levels Each Side" must be at least 1');
            return false;
        }

        // A Type 3 template carries no hand-entered levels; the backend generates the ladder.
        this.selectedTemplateData.levels = [];

        return true;
    }

    @DetectMethodChanges()
    onType3ParameterChange(field: keyof CurrentZoneType3Settings, value: number) {
        if (!this.selectedTemplateData) {
            return;
        }

        if (!this.selectedTemplateData.type3) {
            this.selectedTemplateData.type3 = {};
        }

        this.selectedTemplateData.type3[field] = value;
    }

    @DetectMethodChanges({isAsync: true})
    async onPopupShown() {
    }

    @DetectMethodChanges()
    onPopupHidden() {
        this.popup.visible = false;
        this.validationErrors = {};
    }

    trackByRow(index: number, row: ZoneEditorRow): number {
        return row.sequenceNumber;
    }

    @DetectMethodChanges({isAsync: true})
    async showZonesEditor() {

        if (!this._sessionService.isSuperUser) {
            return;
        }

        this.popover.clearTimeouts();

        // Loaded on every open, not once at init: a grant changed since the last open must be the
        // one the editor honours.
        await this.loadZoneTypeGrants();

        this.popup.visible = true;
    }

    /**
     * CR-20260904-001, Zones Z6. Reads the caller's grant and, for a super user, everybody's.
     * A failure leaves the caller with no grant, which offers no type - it never falls back to
     * offering both.
     */
    private async loadZoneTypeGrants(): Promise<void> {
        try {
            const reply = await this._currentZoneService.getZoneTypeGrants();

            this._isSuperUserGrant = reply.isSuperUser === true;

            const ownUserId = this._sessionService.sessionData.userId;
            const grants = reply.grants || [];
            const own = grants.find(x => x.userId === ownUserId);

            this._ownZoneTypeGrant = own ? own.zoneType : null;

            this.zoneTypeGrantRows = this._currentZoneService.getGrantableUsers()
                .map(user => {
                    const grant = grants.find(x => x.userId === user.userId);
                    return {
                        userId: user.userId,
                        userName: user.userName,
                        zoneType: grant ? grant.zoneType : null
                    };
                });

        } catch (e) {
            this._isSuperUserGrant = false;
            this._ownZoneTypeGrant = null;
            this.zoneTypeGrantRows = [];
            this._toastr.error('Could not read the zone type grants');
        }

        // A type already selected but no longer granted must not stay selected.
        if (!isVoid(this.selectedTemplateType) && !this.isZoneTypeAvailable(this.selectedTemplateType)) {
            this.selectedTemplateType = null;
            this.templateList = [];
            this.selectedTemplate = null;
            this.selectedTemplateData = null;
        }
    }

    @DetectMethodChanges({isAsync: true})
    async onZoneTypeGrantChanged(row: { userId: string, zoneType: ZoneTypeGrantValue },
                                 $event: DxValueChanged<ZoneTypeGrantValue>) {
        const value = isVoid($event.value) ? null : $event.value;

        try {
            await this._currentZoneService.setZoneTypeGrant(row.userId, value);
            row.zoneType = value;

            if (row.userId === this._sessionService.sessionData.userId) {
                this._ownZoneTypeGrant = value;
            }

            // The TEMPLATES column follows the owner's grant, so a grant change on the owner of
            // the highlighted portfolio re-filters it without re-highlighting.
            const selected = this._selectedPortfolioToAssign;

            if (!isVoid(selected) && selected.clientId === row.userId) {
                this.rebuildTemplatesToAssign();
            }
        } catch (e) {
            this._toastr.error('Could not save the zone type grant');
        }
    }

    getLevelTypeText(level: CurrentZoneLevel) {
        if (!isValidNumber(level.price, true)) {
            return undefined;
        }

        return level.price > this.closePrice ? 'Resistance' : 'Support';
    }

    getInputAttrsForLevelType(level: CurrentZoneLevel) {

        let style = 'text-align: center;';

        if (isValidNumber(level.price, true) &&
            (level.price > this.closePrice || level.price < this.closePrice)) {
            if (this.getLevelTypeText(level) === 'Resistance') {
                style += 'color: red;';
            } else {
                style += 'color: green;';
            }
        }

        return {
            style,
        }
    }

    @DetectMethodChanges()
    async onTemplateTypeSelected(type: CurrentZoneTemplateType) {

        // The gate sits here and not only in the template: hiding the button would still leave
        // this method callable.
        if (!this.isZoneTypeAvailable(type)) {
            return;
        }

        this.selectedTemplateType = type;
        this.loading = true;
        setTimeout(async () => {
            try {
                this.templateList = [];
                this.selectedTemplate = null;
                this.selectedTemplateData = null;
                const templates = await this._currentZoneService
                    .getTemplatesByType(this.selectedTemplateType);
                this.templateList = templates;
            } finally {
                this.loading = false;
            }
        });

    }

    @DetectMethodChanges({isAsync: true})
    private async onPortfolioSelected(payload: { portfolio: ApgPortfolio }) {
        this.selectedPortfolio = payload.portfolio;
        const portfolioId = `${payload.portfolio.userId}@${payload.portfolio.id}`;
        await this.loadCurrentZoneData(portfolioId);
    }

    @DetectMethodChanges({isAsync: true})
    private async loadCurrentZoneData(portfolioId: string) {
        const data = await this._currentZoneService.getPortfolioData(portfolioId);
        this.portfolioCurrentZoneData = data;
    }

    async onTemplateSelected($event: DxValueChanged<CurrentZoneTemplateDescriptor>) {

        this.loading = true;

        setTimeout(async () => {
            try {

                if (isVoid($event.value)) {
                    this.selectedTemplate = this.selectedTemplateData = null;
                    return;
                }

                this.selectedTemplate = $event.value;

                if (!isVoid(this.selectedTemplate.id)) {

                    const zoneRequest = this.apiService.getCurrentZone(this.selectedTemplate.id);

                    const dataRequest = this._currentZoneService
                        .getCurrentZoneLevelsTemplateData(this.selectedTemplate.id);

                    const result = await Promise.all([dataRequest, zoneRequest])

                    const data = result[0];
                    const zone = result[1];

                    this.selectedTemplateData = data || ({levels: [], settings: {}} as any);
                    this.selectedTemplateCurrentZone = zone;

                    this.sortRows();
                } else {
                    this.selectedTemplateData = {levels: [], settings: {}} as any;
                    this.selectedTemplateCurrentZone = undefined;

                    if (this.isType3Selected) {
                        this.selectedTemplateData.type3 = {
                            levelsEachSide: CurrentZoneIndicatorComponent.DefaultLevelsEachSide
                        };
                    }
                }

            } finally {
                this.loading = false;
            }
        });
    }

    onNumOfClosesChange(value: number) {
        if (this.selectedTemplateData?.settings) {
            this.selectedTemplateData.settings.numOfCloses = value;
        }
    }

    @DetectMethodChanges({isAsync: true})
    async showAssignTemplatesDialog() {
        this.templatesToAssignLoading = true;

        try {

            const assignmentData =
                await this._currentZoneService.getAssignmentData();

            this.assignmentData = assignmentData;

            const portfolios = this._currentZoneService
                .getAllClientPortfolios();

            const templates = await this._currentZoneService.getAllTemplates();

            portfolios.forEach(portfolio => {
                const key = `${portfolio.clientId}@${portfolio.portfolioId}`;

                let tpls = jsonCloneObject(templates) as CurrentZoneTemplateDescriptor[];

                this._templatesByPortfolio[key] = tpls;

                tpls.forEach(t => {
                    const ix = assignmentData.findIndex(assignment => assignment.templateId === t.id
                        && assignment.portfolioId === key);

                    t.isSelected = ix >= 0;
                });
            });


            const grouped = Enumerable.from(portfolios)
                .groupBy(d => d.clientName)
                .select(grp => {
                    const d = {
                        key: grp.key(),
                        items: grp.toArray(),
                    }
                    return d;
                }).toArray();

            this.clientPortfoliosToAssign = grouped;

            this._selectedPortfolioToAssign = null;
            this.templatesToAssign = [];
            this.templatesToAssignHint = null;

            this.assignTemplatesDialog.visible = true;

        } finally {
            this.templatesToAssignLoading = false;
        }

    }

    // #region Templates To Assign
    @DetectMethodChanges({isAsync: true})
    async saveTemplatesToAssignChanges() {

        // Portfolios that this save newly puts on a Type 2 template. Collected in the same pass
        // that builds the assignments, so the two can never disagree about what was assigned.
        const newlyAssignedToType2: string[] = [];

        const listedPortfolios: ClientPortfolioForCurrentZoneTemplate[] =
            this.clientPortfoliosToAssign.flatMap(x => x.items);

        // The save's scope (CR-20260916-002): every portfolio this dialog listed, ticked or not.
        // The shell rewrites only these portfolios' rows. The dialog lists only this super
        // user's own users, so without the scope the save erased every other super user's rows.
        const portfolioIds = listedPortfolios
            .map(x => `${x.clientId}@${x.portfolioId}`);

        const data = listedPortfolios
            .flatMap((x: ClientPortfolioForCurrentZoneTemplate) => {
                const key = `${x.clientId}@${x.portfolioId}`;
                const tpl = this._templatesByPortfolio[key];
                let assignments = [];
                if (!isVoid(tpl)) {
                    const selected = tpl.filter(x => x.isSelected);

                    const isNewType2 = selected.some(t => t.type === 'Type 2'
                        && !this.wasAlreadyAssigned(key, t.id));

                    if (isNewType2) {
                        newlyAssignedToType2.push(this.describePortfolio(x));
                    }

                    assignments = selected.map(t => {
                        const ass: CurrentZoneTemplateAssignment = {
                            templateId: t.id,
                            portfolioId: key
                        };
                        return ass;
                    });

                }
                return assignments;
            });

        this.templatesToAssignLoading = true;

        await this._currentZoneService.setAssignments(data, portfolioIds)
            .then(_ => {
                this.remindToSetZones(newlyAssignedToType2);

                if (this.selectedPortfolio) {
                    const key = `${this.selectedPortfolio.userId}@${this.selectedPortfolio.id}`;
                    return this.loadCurrentZoneData(key);
                }
            }).finally(() => {
                this.templatesToAssignLoading = false;
                this.closeTemplateAssignDialog();
            });
    }

    /**
     * "Client | Portfolio" for a message the user reads.
     *
     * The default portfolio carries no name of its own, so it falls back to the same
     * `<No Portfolio>` label the portfolio lists show. Without the fallback the line reads
     * "Bob P. |" and names nothing the user can go and act on.
     */
    private describePortfolio(pf: ClientPortfolioForCurrentZoneTemplate): string {
        // CR-20260928-001: every portfolio has a name; the id is the last-resort label.
        const name = isVoid(pf.portfolioName)
            ? pf.portfolioId
            : pf.portfolioName;

        return `${pf.clientName} | ${name}`;
    }

    /**
     * Whether the server already held this portfolio -> template assignment when the dialog was
     * opened. `assignmentData` is that opening snapshot, so anything absent from it is new.
     */
    private wasAlreadyAssigned(portfolioKey: string, templateId: string): boolean {
        return (this.assignmentData || [])
            .some(a => a.portfolioId === portfolioKey && a.templateId === templateId);
    }

    /**
     * Reminds the super user to go and set a starting zone on every portfolio this save newly put
     * on a Type 2 template.
     *
     * Cause -> effect, and the whole reason this toast exists: a Type 2 template does NOT compute
     * a zone, it carries a per-portfolio one stored at `current-zone.zone2.{portfolioKey}`.
     * Assigning the template writes only the assignment row, never that zone, and the movement
     * rule deliberately refuses to invent one (Type2ZoneRule: "0 is not one of the template's
     * levels"). So until somebody picks a level in the zone pop-up for that portfolio, the
     * trader's footer reads N/A and nothing else on their screen shows a zone either. The
     * assignment alone looks finished but is not - hence an explicit reminder.
     *
     * Type 1 is left out on purpose: it derives its own zone and needs no manual step.
     *
     * Sticky by design (no timeout, tap to dismiss): it names portfolios the user must act on,
     * so it must not disappear before it has been read.
     */
    private remindToSetZones(portfolioNames: string[]): void {
        if (isVoid(portfolioNames) || portfolioNames.length === 0) {
            return;
        }

        const list = portfolioNames
            .map(name => `<li>${escapeHtml(name)}</li>`)
            .join('');

        const message = [
            'These portfolios were assigned a <b>Type 2</b> template but have no current zone yet:',
            `<ul>${list}</ul>`,
            'Open the zone pop-up for each one and set its zone explicitly. ',
            'Until you do, the trader will see <b>N/A</b> instead of a zone.'
        ].join('');

        const config: Partial<IndividualConfig> = {
            disableTimeOut: true,
            tapToDismiss: true,
            enableHtml: true,
            positionClass: 'toastr-top-wide-width'
        };

        this._toastr.warning(message, 'Set The Current Zone', config);
    }

    @DetectMethodChanges()
    closeTemplateAssignDialog() {
        this.assignTemplatesDialog.visible = false;
    }

    isTemplatesToAssignListDisabled(item) {

        if (item.isSelected) {
            return false;
        }

        // Checked against the portfolio's FULL template set, not the visible (grant-filtered)
        // list: a template already assigned but hidden by the owner's grant must still hold the
        // one-template-per-portfolio rule, or a second one could be ticked beside it.
        const all = this.selectedPortfolioTemplates();

        return all.some(t => t.isSelected);
    }

    /** The portfolio highlighted in the Assign Templates dialog, or null. */
    private _selectedPortfolioToAssign: ClientPortfolioForCurrentZoneTemplate = null;

    /** Shown in the TEMPLATES column instead of the list, when there is something to explain. */
    templatesToAssignHint: string = null;

    private selectedPortfolioTemplates(): CurrentZoneTemplateDescriptor[] {
        const portfolio = this._selectedPortfolioToAssign;

        if (isVoid(portfolio)) {
            return [];
        }

        const key = `${portfolio.clientId}@${portfolio.portfolioId}`;

        return this._templatesByPortfolio[key] || [];
    }

    /**
     * The grant of the user who owns a portfolio, as the dialog knows it, or undefined when the
     * dialog cannot know it. A super user is sent every row, so for them null means "no grant".
     * A regular user is sent their own row only, so another owner's grant is unknown - not none.
     */
    private grantOfPortfolioOwner(portfolio: ClientPortfolioForCurrentZoneTemplate): ZoneTypeGrantValue | null | undefined {
        if (portfolio.clientId === this._sessionService.sessionData.userId) {
            return this._ownZoneTypeGrant;
        }

        if (!this._isSuperUserGrant) {
            return undefined;
        }

        const row = this.zoneTypeGrantRows.find(r => r.userId === portfolio.clientId);

        return row ? row.zoneType : null;
    }

    @DetectMethodChanges({isAsync: true})
    async onPortfolioToAssignChanged($event: any) {

        const portfolio: ClientPortfolioForCurrentZoneTemplate = $event.addedItems[0];

        this._selectedPortfolioToAssign = isVoid(portfolio) ? null : portfolio;

        if (isVoid(portfolio)) {
            this.templatesToAssign = [];
            this.templatesToAssignHint = null;
            return;
        }

        const key = `${portfolio.clientId}@${portfolio.portfolioId}`

        let templates = this._templatesByPortfolio[key] || [];

        if (isVoid(templates)) {
            templates = await this._currentZoneService.getAllTemplates();

            this._templatesByPortfolio[key] = templates;

            templates.forEach(t => {
                const ix = this.assignmentData
                    .findIndex(x => x.templateId === t.id && x.portfolioId === key);
                t.isSelected = ix > -1;
            });
        }

        this.rebuildTemplatesToAssign();
    }

    /**
     * Builds the TEMPLATES column for the highlighted portfolio: only the template groups whose
     * type the portfolio's OWNER is granted (owner ruling 2026-09-13, bug one of the Assign
     * Templates dialog). Cause -> effect: a template of a type the owner cannot work with cannot
     * be assigned to their portfolio. The filter applies to the super user's own portfolios too.
     */
    private rebuildTemplatesToAssign() {
        const portfolio = this._selectedPortfolioToAssign;

        this.templatesToAssign = [];
        this.templatesToAssignHint = null;

        if (isVoid(portfolio)) {
            return;
        }

        const templates = this.selectedPortfolioTemplates();
        const grant = this.grantOfPortfolioOwner(portfolio);

        // Unknown grant (a regular user looking at another owner's portfolio): nothing to filter
        // by, so the list is shown whole rather than hidden on a guess.
        const grantedTypes = grant === undefined
            ? this._everyZoneType
            : this.zoneTypesOfGrant(grant);

        if (grantedTypes.length === 0) {
            this.templatesToAssignHint = `No zone type is assigned to ${portfolio.clientName}.`;
            return;
        }

        const hiddenAssigned = templates.find(t => t.isSelected && grantedTypes.indexOf(t.type) < 0);

        if (!isVoid(hiddenAssigned)) {
            this.templatesToAssignHint =
                `${this.describePortfolio(portfolio)} is assigned the ${hiddenAssigned.type} template ` +
                `"${hiddenAssigned.name}", which ${portfolio.clientName} is not granted. ` +
                `Grant ${hiddenAssigned.type} to change it.`;
        }

        this.templatesToAssign = Enumerable.from(templates)
            .where(t => grantedTypes.indexOf(t.type) >= 0)
            .groupBy(d => d.type)
            .select(d => {
                return {
                    key: d.key(),
                    items: d.toArray()
                };
            }).toArray();
    }

    @DetectMethodChanges()
    /**
     * A zone was set by hand in the popover. Re-read the portfolio so the footer and the popover
     * show what the backend now serves - for Type 3 that is a ladder regenerated around the new
     * zone (owner ruling 2026-09-15).
     */
    @DetectMethodChanges({isAsync: true})
    async onChange() {
        if (isVoid(this.portfolioCurrentZoneData?.portfolioId)) {
            return;
        }

        await this.loadCurrentZoneData(this.portfolioCurrentZoneData.portfolioId);
    }

    @DetectMethodChanges()
    onNewTemplateClicked() {
        const tpl: CurrentZoneTemplateDescriptor = {
            type: this.selectedTemplateType,
            name: '<Unsaved Template>'
        } as any;
        this.templateList.push(tpl);

        this.selectedTemplate = tpl;
    }

    async onSaveTemplateClicked(makeCopy?: boolean) {

        if (!this.validateAllRows()) {
            return;
        }

        let name: string;
        if (isVoid(this.selectedTemplate.id) || makeCopy) {
            try {

                this.inputDialog.title = "Save Template";
                this.inputDialog.label = 'Provide Template Name';
                this.inputDialog.inputText = null;

                name = await this.inputDialog.show();

                if (!makeCopy) {
                    this.selectedTemplate.name = name;
                }

            } catch {
                return;
            }
        }


        this.loading = true;

        setTimeout(async () => {
            try {

                if (!makeCopy) {
                    const descriptor: CurrentZoneTemplateDescriptor = this.selectedTemplate;

                    if (isVoid(descriptor.id)) {
                        descriptor.id = `${this.selectedTemplateType}.${descriptor.name}`;
                    }

                    const data: CurrentZoneTemplateData = this.selectedTemplateData;

                    await this._currentZoneService.saveTemplate(descriptor, data);

                } else {

                    const descriptor: CurrentZoneTemplateDescriptor = jsonCloneObject(this.selectedTemplate);

                    descriptor.id = null;
                    descriptor.name = name;

                    if (isVoid(descriptor.id)) {
                        descriptor.id = `${this.selectedTemplateType}.${descriptor.name}`;
                    }

                    const data: CurrentZoneTemplateData = jsonCloneObject(this.selectedTemplateData);

                    await this._currentZoneService.saveTemplate(descriptor, data);

                    this.templateList.push(descriptor);

                    this.selectedTemplate = descriptor;
                }

                if (this.selectedPortfolio) {
                    const portfolioId = `${this.selectedPortfolio.userId}@${this.selectedPortfolio.id}`;
                    await this.loadCurrentZoneData(portfolioId);
                }
            } finally {
                this.loading = false;
            }
        });
    }

    @DetectMethodChanges({isAsync: true})
    async onDeleteTemplateClicked() {

        const text = [
            'Are you sure you want to delete',
            `[${this.selectedTemplate?.name}]`,
        ];

        try {
            await this.confirmationDialog.show(text);
        } catch (e) {
            return;
        }

        this.loading = true;

        setTimeout(async () => {
            try {
                await this._currentZoneService.deleteTemplate(this.selectedTemplate.id);
                const tpl = this.selectedTemplate;
                this.selectedTemplate = null;
                const ix = this.templateList.indexOf(tpl);
                if (ix > -1) {
                    this.templateList.splice(ix, 1);
                }

                if (this.selectedPortfolio) {
                    this.portfolioCurrentZoneData = undefined;
                    const portfolioId = `${this.selectedPortfolio.userId}@${this.selectedPortfolio.id}`;
                    await this.loadCurrentZoneData(portfolioId);
                }

            } finally {
                this.loading = false;
            }
        });
    }
}